/* This component uses cockpit layout as base so if cockpit layout changes this component should change too! */
import {Flex, Text, Box, useTheme, Skeleton, HStack, Link} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {useHeader} from '../../../hooks/use-header'
import AddToCartCTA from '../../../components/add-to-cart-cta'
import useMultiSite from '../../../hooks/use-multi-site'
import {getProductsToAddToCart, isProductSet} from '../../../utils/product-utils'
import ProductDisplayPrice from '../../../components/product-display-price'
import useBreakpoints from '../../../hooks/use-breakpoints'

const messages = defineMessages({
    overview: {defaultMessage: 'Overview', id: 'product_detail.sections.overview'},
    details: {defaultMessage: 'Details', id: 'product_detail.sections.details'},
    specifications: {defaultMessage: 'Specifications', id: 'pdp.module.specifications'},
    accessories: {
        defaultMessage: 'Accessories',
        id: 'product_detail.recommended_products.title.accessories'
    },
    moreInfos: {defaultMessage: 'More Infos', id: 'product_detail.sections.moreInfos'},
    reviews: {defaultMessage: 'Reviews', id: 'product_detail.sections.reviews'},
    howToUse: {defaultMessage: 'How To Use', id: 'product_detail.sections.howToUse'},
    spareParts: {defaultMessage: 'Spare Parts', id: 'product_detail.sections.spareParts'},
    productDetailsMenu: {
        defaultMessage: 'Product Details Menu',
        id: 'product_detail.sections.productDetailsMenu'
    }
})

const PdpSubnavigation = ({
    isGetPropsLoading,
    isPromotionsLoading,
    isVariantLoading,
    product,
    quantity,
    sections
}) => {
    const {site} = useMultiSite()
    const {formatMessage} = useIntl()
    const {headerWrapper} = useHeader()
    const headerWrapperHeight = headerWrapper?.clientHeight || 0
    const theme = useTheme()
    const [active, setActive] = useState(null)
    const mobile = useBreakpoints({base: true, lg: false})
    const isProductASet = isProductSet(product)
    /* Anchor offset according dynamic header height */
    const anchorOffset = useRef()
    anchorOffset.current = headerWrapperHeight

    // convert the sections object into an array of objects with id and other properties
    const sectionsList = useMemo(
        () => Object.entries(sections).map(([id, section]) => ({id, ...section})),
        [sections]
    )

    // helper to filter out sections that are incomplete and should not be displayed
    // note this should happen runtime as the sections target and container are refs and can change without rerendering
    const filterIncompleteSections = ({id, container, target}) =>
        messages[id] && target?.current?.id && container?.current

    const totalProducts = useMemo(() => getProductsToAddToCart(product).length, [product])

    const handleScroll = useCallback(() => {
        requestAnimationFrame(() => {
            /* find the first section that is partially visible on the screen */
            const activeSection = sectionsList
                .filter(filterIncompleteSections)
                .find(({container}) => {
                    const current = container?.current
                    if (current) {
                        const start = current.offsetTop - anchorOffset.current
                        const end = current.offsetTop + current.clientHeight - anchorOffset.current

                        return (
                            // the top of the section is above the bottom of the viewport
                            scrollY + innerHeight - anchorOffset.current >= start &&
                            // the bottom of the section is below the top of the viewport
                            scrollY <= end
                        )
                    }
                })

            if (activeSection) {
                setActive(activeSection.id)
            } else {
                setActive(null)
            }
        })
    }, [sectionsList])

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, {
            passive: true
        })

        /* Cleanup event listener */
        return () => window.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    return (
        <Box
            maxWidth={'100%'}
            mx="auto"
            bg="stokkeGray.bright"
            boxShadow={theme.shadows.bottom}
            minHeight="var(--internal-pdp-subnavigation-height)"
        >
            <Box
                padding={{
                    base: 4,
                    lg: '0.75rem 1rem 0.75rem 1.5rem'
                }}
                background="stokkeGray.bright"
                overflowX={{
                    base: 'auto',
                    lg: 'hidden'
                }}
                transition=".5s"
                sx={{
                    '&>*': {
                        flexShrink: 0
                    }
                }}
                scrollBehavior="smooth"
                maxWidth="container.xxl"
                mx="auto"
            >
                <Flex justify="space-between" align="center">
                    <Flex gap={4} as="nav" aria-label={formatMessage(messages.productDetailsMenu)}>
                        {!sectionsList.filter(filterIncompleteSections)?.length ? ( //this should happen runtime, check filterIncompleteSections
                            <>
                                <Skeleton height="20px" width="80px" />
                                <Skeleton height="20px" width="80px" />
                                <Skeleton height="20px" width="80px" />
                                <Skeleton height="20px" width="80px" />
                                <Skeleton height="20px" width="80px" />
                            </>
                        ) : (
                            sectionsList.filter(filterIncompleteSections).map(
                                (
                                    {id, target} //this should happen runtime
                                ) => (
                                    <Link
                                        key={id}
                                        size="sm"
                                        variant="core"
                                        textDecoration={active === id ? 'underline' : 'none'}
                                        aria-current={active === id ? 'true' : 'false'}
                                        _hover={mobile ? {} : null}
                                        href={`#${target.current.id}`}
                                    >
                                        <FormattedMessage {...messages[id]} />
                                    </Link>
                                )
                            )
                        )}
                    </Flex>
                    <Box display={{base: 'none', lg: 'block'}}>
                        <Flex gap={2} align="center" justifyContent="flex-end">
                            <Flex flexDirection="column">
                                {isProductASet && (
                                    <HStack
                                        justify="start"
                                        alignItems="flex-start"
                                        spacing={0}
                                        gap={1.5}
                                    >
                                        <Text textStyle="Regular Extra Small">
                                            <FormattedMessage
                                                id="global.total"
                                                defaultMessage="Total"
                                            />
                                        </Text>
                                        <Skeleton isLoaded={!isGetPropsLoading}>
                                            <Text
                                                textStyle="Regular Extra Small"
                                                whiteSpace="nowrap"
                                                color="stokkeGray.accessibleText"
                                            >
                                                <FormattedMessage
                                                    id="product_set_detail.cockpit.product-count"
                                                    defaultMessage="{totalProducts} products"
                                                    values={{totalProducts}}
                                                />
                                            </Text>
                                        </Skeleton>
                                    </HStack>
                                )}
                                {!site?.isContentOnlySite && (
                                    <Flex
                                        flexDirection="column"
                                        flexBasis="min-content"
                                        alignItems="end"
                                    >
                                        <ProductDisplayPrice
                                            product={product}
                                            loading={
                                                isGetPropsLoading ||
                                                isPromotionsLoading ||
                                                isVariantLoading
                                            }
                                            variant="stickyCta"
                                        />
                                    </Flex>
                                )}
                            </Flex>
                            <Flex flexBasis="content">
                                <AddToCartCTA
                                    isLoading={
                                        isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                    }
                                    product={product}
                                    quantity={quantity}
                                />
                            </Flex>
                        </Flex>
                    </Box>
                </Flex>
            </Box>
        </Box>
    )
}

PdpSubnavigation.propTypes = {
    isGetPropsLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    product: PropTypes.object,
    quantity: PropTypes.number,
    sections: PropTypes.object
}

export default PdpSubnavigation
